import { useState, useEffect, useRef, useCallback } from 'react';
import { DashboardResponse } from '../types/dashboard';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const RETRY_DELAY_MS = 10_000;

interface UseDashboardPollingResult {
  data: DashboardResponse | null;
  isOffline: boolean;
  error: Error | null;
}

export function useDashboardPolling(
  area: string,
  intervalMs: number = 30_000,
): UseDashboardPollingResult {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [isOffline, setIsOffline] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const intervalRef = useRef<number | null>(null);
  const retryTimeoutRef = useRef<number | null>(null);

  const clearRetryTimeout = useCallback(() => {
    if (retryTimeoutRef.current !== null) {
      window.clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/public/dashboard/${area}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json: DashboardResponse = await response.json();
      setData(json);
      setIsOffline(false);
      setError(null);
      clearRetryTimeout();
    } catch (err) {
      setIsOffline(true);
      setError(err instanceof Error ? err : new Error(String(err)));
      // Schedule retry after delay — retain last valid data
      clearRetryTimeout();
      retryTimeoutRef.current = window.setTimeout(() => {
        fetchData();
      }, RETRY_DELAY_MS);
    }
  }, [area, clearRetryTimeout]);

  useEffect(() => {
    // Initial fetch
    fetchData();

    // Set up polling interval
    intervalRef.current = window.setInterval(fetchData, intervalMs);

    // Resume polling on visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      clearRetryTimeout();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [area, intervalMs, fetchData, clearRetryTimeout]);

  return { data, isOffline, error };
}
